import { ServerError } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { toast } from "sonner";

import { pathTo } from "@/utils/pathGenerators";

export const redirectAllowList = [
  "/login",
  "/logout",
  "/reset-password",
  "/invites",
  "/register",
  "/ai/register",
  "/vercel-integration",
  "/pricing",
  "/public",
];

// Segment-aware allow-list match: true when pathname equals an allow-listed
// path exactly, or is nested beneath it (path + "/"). This prevents a bare
// prefix like "/publicity" from matching "/public".
export const isRedirectAllowedPath = (pathname: string, allowList = redirectAllowList) =>
  allowList.some(path => pathname === path || pathname.startsWith(`${path}/`));

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError, response, forward, operation }) => {
  // Redirect to login flow if user is not authenticated

  if (networkError) {
    const statusCode = (networkError as ServerError).statusCode;

    if (typeof window !== "undefined" && !isRedirectAllowedPath(window.location.pathname)) {
      if (statusCode === 401) {
        window.location.replace(pathTo("/login", { search: { next: window.location.href } }));
        return;
      } else if (statusCode === 403) {
        window.location.replace("/login/disabled");
        return;
      }
    }
    return;
  }

  const unconfirmedAllowList = [...redirectAllowList, "/register/verify"];
  const parkedAccountAllowList = [...redirectAllowList, "/parked-account"];
  if (graphQLErrors) {
    const unverifiedError = graphQLErrors.find(error => error.extensions?.code === "UNVERIFIED_USER");
    if (
      typeof window !== "undefined" &&
      !isRedirectAllowedPath(window.location.pathname, unconfirmedAllowList) &&
      unverifiedError
    ) {
      window.location.replace(
        pathTo("/register/verify", {
          keepQuery: true,
          search: {
            email: unverifiedError.extensions.email as string,
          },
        })
      );
      return;
    }

    const parkedAccountError = graphQLErrors.find(error => error.extensions?.code === "ACCOUNT_PARKED");
    if (
      typeof window !== "undefined" &&
      !isRedirectAllowedPath(window.location.pathname, parkedAccountAllowList) &&
      parkedAccountError
    ) {
      window.location.replace("/parked-account");
      return;
    }

    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      // Don't show toast for errors that trigger redirects
      if (extensions?.code === "ACCOUNT_PARKED" || extensions?.code === "UNVERIFIED_USER") {
        return;
      }
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
      toast.error("An unexpected error occurred", {
        id: message,
        description: message,
      });
    });
  }

  forward(operation);
});

export default errorLink;
